import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule } from 'ag-grid-community';
import { ModuleRegistry, themeBalham } from 'ag-grid-community';
import { format } from 'date-fns';
import { DetailLivraison } from '../../../utils/types/DetailLivraison';
import PriseDetails from '../../modals/details/PriseDetails';
import { IClient } from '../../../utils/types/IClient';
import CarteDetails from '../../modals/details/CarteDetails';
import { Carte } from '../../../utils/types/Carte';
import * as XLSX from 'xlsx';

ModuleRegistry.registerModules([AllCommunityModule]);

interface PriseGridProps {
    prises: DetailLivraison[];
    client: IClient | null;
    adminOrCommercial?: boolean;
}

const PriseGrid: React.FC<PriseGridProps> = ({ prises, client, adminOrCommercial = false }) => {
    const gridRef = useRef<AgGridReact>(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [columnDefs, setColumnDefs] = useState<ColDef<DetailLivraison>[]>([
        {
            field: 'codeManuel',
            headerName: 'No Bon Livraison',
            cellRenderer: (params: any) => <PriseDetails prise={params.data} client={client} />,
            pinned: isMobile ? undefined : 'left',
            sortable: true,
            filter: true,
        },
        {
            field: 'dateDocument',
            headerName: 'Date',
            sortable: true,
            valueFormatter: (params) => (params.value ? format(params.value, 'dd/MM/yyyy') : ''),
            filter: 'agDateColumnFilter',
        },
        { field: 'pointDeVente', headerName: 'Station', sortable: true, filter: true },
        { field: 'designation', headerName: 'Produit', sortable: true, filter: true },
        { field: 'typeLivraison', headerName: 'Type Prise', sortable: true, filter: true },
        {
            field: 'numeroCarte',
            headerName: 'No Carte',
            cellRenderer: (params: any) => {
                const numeroCarte = params.data?.numeroCarte;
                return <CarteDetails pCarte={new Carte(numeroCarte)} codeClient={client?.cardCode ?? ''} onUpdate={() => {}} />;
            },
            sortable: true,
            filter: true,
        },
        { field: 'porteurCarte', headerName: 'Porteur', sortable: true, filter: true },
        {
            field: 'quantite',
            headerName: 'Quantité',
            sortable: true,
            valueFormatter: (params) => (params.value ? `${params.value.toFixed(2).replace('.', ',')} L` : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'prixDol',
            headerName: 'Prix Unitaire',
            sortable: true,
            valueFormatter: (params) => (params.value ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        {
            headerName: 'Total',
            sortable: true,
            valueGetter: (params) => {
                const data = params.data as DetailLivraison;
                return data.quantite != null && data.prixDol != null ? data.quantite * data.prixDol : 0;
            },
            valueFormatter: (params) => (params.value ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        { field: 'refClient', headerName: 'Chauffeur', sortable: true, filter: true },
        { field: 'refVehicule', headerName: 'Numéro Plaque', sortable: true, filter: true },
        {
            field: 'kilometrage',
            headerName: 'Kilometrage',
            sortable: true,
            valueFormatter: (params) => (params.value ? `${params.value.toFixed(2).replace('.', ',')}` : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },

        {
            field: 'dateEnregistrementLivraison',
            headerName: 'Date Enregistrement',
            sortable: true,
            valueFormatter: (params) => (params.value ? format(params.value, 'dd/MM/yyyy HH:mm') : ''),
            filter: 'agDateColumnFilter',
            cellStyle: { fontStyle: 'italic' },
        },
        {
            field: 'codeShift',
            headerName: 'Code Shift',
            sortable: true,
            filter: true,
            cellStyle: { fontStyle: 'italic' },
        },
        {
            field: 'agent',
            headerName: 'Pompiste',
            sortable: true,
            filter: true,
            cellStyle: { fontStyle: 'italic' },
        },
        {
            field: 'codeManuelTerminal',
            headerName: 'Code Terminal',
            sortable: true,
            filter: true,
            cellStyle: { fontStyle: 'italic' },
        },
    ]);

    // ---

    const myTheme = themeBalham.withParams({
        columnBorder: { style: 'dashed', width: '1px' },
    });

    const exportAsCsv = useCallback(() => {
        if (!gridRef.current) return;

        const visibleColumns = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleColumns || visibleColumns.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const columnKeys = visibleColumns.map((col) => col.getColId());

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

        gridRef.current.api.exportDataAsCsv({
            fileName: `prises_export_${timestamp}.csv`,
            allColumns: false,
            columnKeys,
            suppressQuotes: false,
        });
    }, [gridRef]);

    const exportAsExcel = useCallback(() => {
        if (!gridRef.current) return;

        const visibleColumns = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleColumns || visibleColumns.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const rowData: DetailLivraison[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
            rowData.push(node.data);
        });

        if (rowData.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }

        // Construire exportData avec uniquement colonnes visibles
        const exportData = rowData.map((row) => {
            const rowObj: Record<string, any> = {};

            visibleColumns.forEach((col) => {
                const colDef = col.getColDef();
                const field = colDef.field;
                const headerName = colDef.headerName ?? field ?? '';

                if (!field) return;

                let value = (row as any)[field];

                // Appliquer les mêmes formatages que dans la grille selon le champ

                if (field === 'dateDocument' || field === 'dateEnregistrementLivraison') {
                    value = value ? format(value, field === 'dateDocument' ? 'dd/MM/yyyy' : 'dd/MM/yyyy HH:mm') : '';
                } else if (field === 'quantite' || field === 'kilometrage') {
                    value = value != null ? value.toFixed(2).replace('.', ',') : '';
                } else if (field === 'prixDol') {
                    value = value != null ? value.toFixed(2) : '';
                } else if (headerName === 'Total') {
                    // Calculer total si existant
                    const total = row.quantite != null && row.prixDol != null ? row.quantite * row.prixDol : 0;
                    value = total.toFixed(2);
                }

                rowObj[headerName] = value;
            });

            return rowObj;
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Prises');

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        XLSX.writeFile(workbook, `prises_export_${timestamp}.xlsx`);
    }, [gridRef]);

    // ---

    useEffect(() => {
        setColumnDefs((prevDefs) =>
            prevDefs.map((col) => {
                if (['soldeCarte', 'dateEnregistrementLivraison', 'codeShift', 'agent', 'codeManuelTerminal'].includes(col.field ?? '')) {
                    return { ...col, hide: !adminOrCommercial };
                }

                return col;
            })
        );
    }, [adminOrCommercial]);

    // ---

    return (
        <>
            <div className="" style={{ height: '100vh', width: '100%' }}>
                <div style={{ height: '100%', width: '100%' }}>
                    <AgGridReact
                        ref={gridRef}
                        theme={myTheme}
                        getRowId={(params) => params.data.id.toString()}
                        rowData={prises}
                        columnDefs={columnDefs}
                        pagination={true}
                        domLayout="normal"
                        defaultColDef={{
                            resizable: true,
                            editable: false,
                            minWidth: 100,
                            flex: 1,
                        }}
                        onGridReady={(params) => {
                            params.api.autoSizeAllColumns();
                        }}
                        suppressExcelExport={true}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                        animateRows={true}
                        overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>"
                    />
                </div>
            </div>

            <div className="dropdown mt-1">
                <button className="btn dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown">
                    Export
                </button>

                <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                    <li>
                        <button className="dropdown-item" onClick={exportAsCsv}>
                            Export as CSV
                        </button>
                        <button className="dropdown-item" onClick={exportAsExcel}>
                            Export as Excel
                        </button>
                    </li>
                </ul>
            </div>
        </>
    );
};

export default PriseGrid;
